import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getValidToken, API_URL } from './utils/authUtils';
import AdminRouteGuard from './components/AdminRouteGuard';
import { ADMIN_SECTIONS, clearPermissionsCache } from './utils/adminPermissions';
import { ResponsiveTable, MobileAdminUserCard, MobileSidebar } from './components/ResponsiveComponents';

const BACKEND_URL = API_URL;

const AdminUserManagement = () => {
  const navigate = useNavigate();
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  
  // Dashboard Statistics state
  const [dashboardStats, setDashboardStats] = useState(null);
  const [dashboardStatsLoading, setDashboardStatsLoading] = useState(false);
  const [lastDashboardUpdate, setLastDashboardUpdate] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    is_super_admin: false,
    is_active: true,
    // All permissions start as false (no sections hidden)
    hide_recruiter_management: false,
    hide_student_management: false,
    hide_student_records: false,
    hide_partner_management: false,
    hide_token_management: false,
    hide_user_activity: false,
    hide_company_management: false,
    hide_alumni_management: false,
    hide_map_view: false,
    hide_blog_management: false,
    hide_job_analytics: false,
    hide_content_curation: false,
    hide_curator_management: true,  // Default hidden for security (super admin only)
    hide_feature_control: true,  // Default hidden for security
    hide_business_development: false,
    hide_verification_dashboard: false,
    hide_system_settings: false,
    hide_platform_settings: true,  // Default hidden for security
    hide_membership_manager: true,  // Default hidden for security
    hide_crm: true,  // Default hidden for security
    hide_swee: true,  // Default hidden for security (SWEE)
    hide_executive_intelligence: true,  // Default hidden for security (page-level)
    hide_alumni_dash_admin: true,  // Default hidden for security
    hide_grant_management: true,  // Default hidden for security (Grant Management)
    hide_recruiter_tokens: false,
    hide_student_tokens: false,
    hide_partner_tokens: false,
    hide_recruiter_activity: false,
    hide_student_activity: false,
    hide_partner_activity: false,
    // Executive Intelligence tab permissions
    hide_exec_dashboard: false,
    hide_exec_search: false,
    hide_exec_map: false,
    hide_exec_favorites: false,
    hide_exec_imports: false,
    hide_exec_reviews: false,
    hide_exec_scoring: false,
    hide_exec_calibration: false,
    hide_exec_reports: false,
    hide_exec_analytics: false,
    hide_exec_brokerage: false,
    // SMS notifications
    sms_phone: '',
    sms_enabled: false
  });

  useEffect(() => {
    fetchAdminUsers();
    fetchDashboardStats();
  }, []);

  // Auto-refresh dashboard stats every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardStats();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, []);

  const fetchAdminUsers = async () => {
    try {
      const token = getValidToken(); // Use existing token system
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/admin-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setAdminUsers(response.data);
    } catch (err) {
      console.error('Error fetching admin users:', err);
      setError('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  // Fetch dashboard statistics
  const fetchDashboardStats = async () => {
    setDashboardStatsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/dashboard-stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (response.data) {
        setDashboardStats(response.data);
        setLastDashboardUpdate(new Date());
        console.log('Dashboard stats fetched:', response.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setDashboardStats(null);
    } finally {
      setDashboardStatsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const token = getValidToken(); // Use existing token system
      
      const endpoint = editingUser 
        ? `${BACKEND_URL}/api/admin/admin-users/${editingUser.id}`
        : `${BACKEND_URL}/api/admin/admin-users`;
      
      const method = editingUser ? 'put' : 'post';
      
      await axios[method](endpoint, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      await fetchAdminUsers();
      resetForm();
      alert(editingUser ? 'Admin user updated successfully!' : 'Admin user created successfully!');
    } catch (err) {
      console.error('Error saving admin user:', err);
      setError(err.response?.data?.detail || 'Failed to save admin user');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      full_name: '',
      is_super_admin: false,
      is_active: true,
      hide_recruiter_management: false,
      hide_student_management: false,
      hide_student_records: false,
      hide_partner_management: false,
      hide_token_management: false,
      hide_user_activity: false,
      hide_company_management: false,
      hide_alumni_management: false,
      hide_map_view: false,
      hide_blog_management: false,
      hide_job_analytics: false,
      hide_content_curation: false,
      hide_curator_management: true,  // Default hidden for security (super admin only)
      hide_feature_control: true,  // Default hidden for security
      hide_business_development: false,
      hide_verification_dashboard: false,
      hide_system_settings: false,
      hide_platform_settings: true,  // Default hidden for security
      hide_crm: true,  // Default hidden for security
      hide_swee: true,  // Default hidden for security (SWEE)
      hide_alumni_dash_admin: true,  // Default hidden for security
      hide_grant_management: true,  // Default hidden for security (Grant Management)
      hide_recruiter_tokens: false,
      hide_student_tokens: false,
      hide_partner_tokens: false,
      hide_recruiter_activity: false,
      hide_student_activity: false,
      hide_partner_activity: false,
      hide_exec_dashboard: false,
      hide_exec_search: false,
      hide_exec_map: false,
      hide_exec_favorites: false,
      hide_exec_imports: false,
      hide_exec_reviews: false,
      hide_exec_scoring: false,
      hide_exec_calibration: false,
      hide_exec_reports: false,
      hide_exec_analytics: false,
      hide_exec_brokerage: false,
      sms_phone: '',
      sms_enabled: false
    });
    setEditingUser(null);
    setShowCreateForm(false);
  };

  const handleEdit = (user) => {
    setFormData({
      username: user.username,
      email: user.email,
      password: '', // Don't pre-fill password
      full_name: user.full_name,
      is_super_admin: user.is_super_admin,
      is_active: user.is_active,
      hide_recruiter_management: user.hide_recruiter_management,
      hide_student_management: user.hide_student_management,
      hide_student_records: user.hide_student_records,
      hide_partner_management: user.hide_partner_management,
      hide_token_management: user.hide_token_management,
      hide_user_activity: user.hide_user_activity,
      hide_company_management: user.hide_company_management,
      hide_alumni_management: user.hide_alumni_management,
      hide_map_view: user.hide_map_view,
      hide_blog_management: user.hide_blog_management,
      hide_job_analytics: user.hide_job_analytics,
      hide_content_curation: user.hide_content_curation,
      hide_curator_management: user.hide_curator_management,
      hide_feature_control: user.hide_feature_control,
      hide_business_development: user.hide_business_development,
      hide_verification_dashboard: user.hide_verification_dashboard,
      hide_system_settings: user.hide_system_settings,
      hide_platform_settings: user.hide_platform_settings,
      hide_membership_manager: user.hide_membership_manager,
      hide_crm: user.hide_crm ?? true,
      hide_swee: user.hide_swee ?? true,
      hide_executive_intelligence: user.hide_executive_intelligence ?? true,
      hide_alumni_dash_admin: user.hide_alumni_dash_admin ?? true,
      hide_grant_management: user.hide_grant_management ?? true,
      hide_recruiter_tokens: user.hide_recruiter_tokens,
      hide_student_tokens: user.hide_student_tokens,
      hide_partner_tokens: user.hide_partner_tokens,
      hide_recruiter_activity: user.hide_recruiter_activity,
      hide_student_activity: user.hide_student_activity,
      hide_partner_activity: user.hide_partner_activity,
      hide_exec_dashboard: user.hide_exec_dashboard || false,
      hide_exec_search: user.hide_exec_search || false,
      hide_exec_map: user.hide_exec_map || false,
      hide_exec_favorites: user.hide_exec_favorites || false,
      hide_exec_imports: user.hide_exec_imports || false,
      hide_exec_reviews: user.hide_exec_reviews || false,
      hide_exec_scoring: user.hide_exec_scoring || false,
      hide_exec_calibration: user.hide_exec_calibration || false,
      hide_exec_reports: user.hide_exec_reports || false,
      hide_exec_analytics: user.hide_exec_analytics || false,
      hide_exec_brokerage: user.hide_exec_brokerage || false,
      sms_phone: user.sms_phone || '',
      sms_enabled: user.sms_enabled || false
    });
    setEditingUser(user);
    setShowCreateForm(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this admin user?')) return;

    try {
      const token = getValidToken(); // Use existing token system
      await axios.delete(`${BACKEND_URL}/api/admin/admin-users/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      await fetchAdminUsers();
      alert('Admin user deleted successfully!');
    } catch (err) {
      console.error('Error deleting admin user:', err);
      setError(err.response?.data?.detail || 'Failed to delete admin user');
    }
  };

  const scrollToPlatformOverview = () => {
    const platformOverviewElement = document.getElementById('platform-overview');
    if (platformOverviewElement) {
      platformOverviewElement.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <AdminRouteGuard requiredPermission={ADMIN_SECTIONS.SYSTEM_SETTINGS}>
      {/* Mobile Sidebar */}
      <MobileSidebar
        activeTab="user-management"
        onTabChange={(tabId) => {
          if (tabId === 'home') {
            navigate('/admin');
          } else if (tabId === 'students') {
            // Students button handled by grid menu
          } else if (tabId === 'recruiters') {
            navigate('/admin?tab=recruiters');
          } else if (tabId === 'profile') {
            // Logout functionality
            localStorage.removeItem('token');
            localStorage.removeItem('authToken');
            clearPermissionsCache();
            navigate('/');
          }
        }}
        navigate={navigate}
        isOpen={isMobileSidebarOpen}
        onToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col gap-4">
            {/* Header Info */}
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Admin User Management</h1>
              <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
                Manage admin users and their permissions
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end">
              <button
                onClick={() => setShowCreateForm(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <span className="hidden sm:inline">Add Admin User</span>
                <span className="sm:hidden">Add User</span>
              </button>
              <button
                onClick={() => navigate('/admin')}
                className="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <span className="hidden sm:inline">Back to Admin Panel</span>
                <span className="sm:hidden">Back to Admin</span>
              </button>
              <button
                onClick={scrollToPlatformOverview}
                className="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span className="hidden sm:inline">Platform Overview</span>
                <span className="sm:hidden">Overview</span>
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem('token');
                  localStorage.removeItem('authToken');
                  clearPermissionsCache();
                  navigate('/');
                  setTimeout(() => {
                    alert('Successfully logged out');
                  }, 100);
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        </div>

      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Simple Create/Edit Form */}
      {showCreateForm && (
        <div className="mb-6 sm:mb-8 bg-white rounded-lg shadow p-4 sm:p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            {editingUser ? 'Edit Admin User' : 'Create New Admin User'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Username</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {editingUser ? 'New Password (leave blank to keep current)' : 'Password'}
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUser}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Active Account</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_super_admin"
                  checked={formData.is_super_admin}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Super Admin</span>
              </label>
            </div>

            {/* SMS Notifications Section */}
            <div className="pt-4 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">SMS Notifications</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                  <input
                    type="tel"
                    name="sms_phone"
                    value={formData.sms_phone}
                    onChange={handleInputChange}
                    placeholder="+1234567890"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="sms_enabled"
                      checked={formData.sms_enabled}
                      onChange={handleInputChange}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Enable SMS Alerts</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Permissions Section */}
            <div className="pt-4 border-t border-gray-200">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Section Access Permissions</h4>
              <p className="text-sm text-gray-600 mb-4">
                Check boxes to <strong>hide</strong> sections from this admin user. Unchecked sections will be visible.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {/* Core Management Sections */}
                <div>
                  <h5 className="font-medium text-gray-800 mb-3">Core Management</h5>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_recruiter_management"
                        checked={formData.hide_recruiter_management}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Recruiter Management</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_student_management"
                        checked={formData.hide_student_management}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Student Management</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_student_records"
                        checked={formData.hide_student_records}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Student Records</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_partner_management"
                        checked={formData.hide_partner_management}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Partner Management</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_company_management"
                        checked={formData.hide_company_management}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Company Management</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_alumni_management"
                        checked={formData.hide_alumni_management}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Alumni Management</span>
                    </label>
                  </div>
                </div>

                {/* Advanced Admin Tools */}
                <div>
                  <h5 className="font-medium text-gray-800 mb-3">Advanced Admin Tools</h5>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_map_view"
                        checked={formData.hide_map_view}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide MapView Access</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_blog_management"
                        checked={formData.hide_blog_management}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Blog Management</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_job_analytics"
                        checked={formData.hide_job_analytics}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Job Analytics</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_content_curation"
                        checked={formData.hide_content_curation}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Content Curation</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_curator_management"
                        checked={formData.hide_curator_management}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Curator Management</span>
                      <span className="ml-2 text-xs text-red-500">(Super Admin Only)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_business_development"
                        checked={formData.hide_business_development}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Business Development</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_feature_control"
                        checked={formData.hide_feature_control}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Feature Control</span>
                      <span className="ml-2 text-xs text-red-500">(High Security)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_platform_settings"
                        checked={formData.hide_platform_settings}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Platform Settings</span>
                      <span className="ml-2 text-xs text-red-500">(High Security)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_membership_manager"
                        checked={formData.hide_membership_manager}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Membership Manager</span>
                      <span className="ml-2 text-xs text-red-500">(High Security)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_crm"
                        checked={formData.hide_crm}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide CRM</span>
                      <span className="ml-2 text-xs text-red-500">(High Security)</span>
                    </label>
                  </div>
                </div>

                {/* System & Activity Sections */}
                <div>
                  <h5 className="font-medium text-gray-800 mb-3">System & Activity</h5>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_verification_dashboard"
                        checked={formData.hide_verification_dashboard}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Verification Dashboard</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_user_activity"
                        checked={formData.hide_user_activity}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide User Activity</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_token_management"
                        checked={formData.hide_token_management}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Token Management</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_system_settings"
                        checked={formData.hide_system_settings}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide System Settings (Admin Users)</span>
                    </label>
                  </div>
                </div>

                {/* Fine-grained Token Permissions */}
                <div>
                  <h5 className="font-medium text-gray-800 mb-3">Fine-grained Token Access</h5>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_recruiter_tokens"
                        checked={formData.hide_recruiter_tokens}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Recruiter Tokens</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_student_tokens"
                        checked={formData.hide_student_tokens}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Student Tokens</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_partner_tokens"
                        checked={formData.hide_partner_tokens}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Partner Tokens</span>
                    </label>
                  </div>
                </div>

                {/* Fine-grained Activity Permissions */}
                <div>
                  <h5 className="font-medium text-gray-800 mb-3">Fine-grained Activity Access</h5>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_recruiter_activity"
                        checked={formData.hide_recruiter_activity}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Recruiter Activity</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_student_activity"
                        checked={formData.hide_student_activity}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Student Activity</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_partner_activity"
                        checked={formData.hide_partner_activity}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Partner Activity</span>
                    </label>
                  </div>
                </div>

                {/* Executive Intelligence Tab Permissions */}
                <div className="sm:col-span-2">
                  <h5 className="font-medium text-indigo-800 mb-3">Executive Intelligence Tabs</h5>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_exec_dashboard"
                        checked={formData.hide_exec_dashboard}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Dashboard</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_exec_search"
                        checked={formData.hide_exec_search}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Search</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_exec_map"
                        checked={formData.hide_exec_map}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Map</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_exec_favorites"
                        checked={formData.hide_exec_favorites}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Favorites</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_exec_imports"
                        checked={formData.hide_exec_imports}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Imports</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_exec_reviews"
                        checked={formData.hide_exec_reviews}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Reviews</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_exec_scoring"
                        checked={formData.hide_exec_scoring}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Scoring</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_exec_calibration"
                        checked={formData.hide_exec_calibration}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Calibration</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_exec_reports"
                        checked={formData.hide_exec_reports}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Reports</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_exec_analytics"
                        checked={formData.hide_exec_analytics}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Analytics</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="hide_exec_brokerage"
                        checked={formData.hide_exec_brokerage}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide Brokerage</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* SWEE Permission */}
              <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200 mt-3">
                <h4 className="text-sm font-medium text-emerald-800 mb-2">SWEE (Strategic Workforce Evolution Engine)</h4>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="hide_swee"
                    checked={formData.hide_swee}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Hide SWEE</span>
                </label>
              </div>

              {/* Executive Intelligence Permission */}
              <div className="bg-violet-50 p-3 rounded-lg border border-violet-200 mt-3">
                <h4 className="text-sm font-medium text-violet-800 mb-2">Executive Intelligence Platform</h4>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="hide_executive_intelligence"
                    checked={formData.hide_executive_intelligence}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Hide Executive Intelligence</span>
                </label>
              </div>

              {/* Alumni Dash Admin Permission */}
              <div className="bg-sky-50 p-3 rounded-lg border border-sky-200 mt-3">
                <h4 className="text-sm font-medium text-sky-800 mb-2">Alumni Dashboard Administration</h4>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="hide_alumni_dash_admin"
                    checked={formData.hide_alumni_dash_admin}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Hide Alumni Dash Admin</span>
                </label>
              </div>

              {/* Grant Management Permission */}
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 mt-3">
                <h4 className="text-sm font-medium text-amber-800 mb-2">Grant Management</h4>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="hide_grant_management"
                    checked={formData.hide_grant_management}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Hide Grant Management</span>
                </label>
              </div>

              {/* Quick Actions */}
              <div className="mt-4 pt-3 border-t border-gray-100">
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      // Set all hide permissions to false (show all sections)
                      setFormData(prev => ({
                        ...prev,
                        hide_recruiter_management: false,
                        hide_student_management: false,
                        hide_student_records: false,
                        hide_partner_management: false,
                        hide_token_management: false,
                        hide_user_activity: false,
                        hide_company_management: false,
                        hide_alumni_management: false,
                        hide_map_view: false,
                        hide_blog_management: false,
                        hide_job_analytics: false,
                        hide_content_curation: false,
                        hide_curator_management: false,
                        hide_feature_control: false,
                        hide_business_development: false,
                        hide_platform_settings: false,
                        hide_membership_manager: false,
                        hide_crm: false,
                        hide_swee: false,
                        hide_executive_intelligence: false,
                        hide_alumni_dash_admin: false,
                        hide_grant_management: false,
                        hide_verification_dashboard: false,
                        hide_system_settings: false,
                        hide_recruiter_tokens: false,
                        hide_student_tokens: false,
                        hide_partner_tokens: false,
                        hide_recruiter_activity: false,
                        hide_student_activity: false,
                        hide_partner_activity: false,
                        hide_exec_dashboard: false,
                        hide_exec_search: false,
                        hide_exec_map: false,
                        hide_exec_favorites: false,
                        hide_exec_imports: false,
                        hide_exec_reviews: false,
                        hide_exec_scoring: false,
                        hide_exec_calibration: false,
                        hide_exec_reports: false,
                        hide_exec_analytics: false,
                        hide_exec_brokerage: false
                      }));
                    }}
                    className="w-full sm:w-auto px-3 py-2 text-xs sm:text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                  >
                    Show All Sections
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Set all hide permissions to true (hide all sections)
                      setFormData(prev => ({
                        ...prev,
                        hide_recruiter_management: true,
                        hide_student_management: true,
                        hide_student_records: true,
                        hide_partner_management: true,
                        hide_token_management: true,
                        hide_user_activity: true,
                        hide_company_management: true,
                        hide_alumni_management: true,
                        hide_map_view: true,
                        hide_blog_management: true,
                        hide_job_analytics: true,
                        hide_content_curation: true,
                        hide_curator_management: true,
                        hide_feature_control: true,
                        hide_business_development: true,
                        hide_platform_settings: true,
                        hide_membership_manager: true,
                        hide_crm: true,
                        hide_swee: true,
                        hide_executive_intelligence: true,
                        hide_alumni_dash_admin: true,
                        hide_grant_management: true,
                        hide_verification_dashboard: true,
                        hide_system_settings: true,
                        hide_recruiter_tokens: true,
                        hide_student_tokens: true,
                        hide_partner_tokens: true,
                        hide_recruiter_activity: true,
                        hide_student_activity: true,
                        hide_partner_activity: true,
                        hide_exec_dashboard: true,
                        hide_exec_search: true,
                        hide_exec_map: true,
                        hide_exec_favorites: true,
                        hide_exec_imports: true,
                        hide_exec_reviews: true,
                        hide_exec_scoring: true,
                        hide_exec_calibration: true,
                        hide_exec_reports: true,
                        hide_exec_analytics: true,
                        hide_exec_brokerage: true
                      }));
                    }}
                    className="w-full sm:w-auto px-3 py-2 text-xs sm:text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                  >
                    Hide All Sections
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={resetForm}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <span className="hidden sm:inline">{editingUser ? 'Update Admin User' : 'Create Admin User'}</span>
                <span className="sm:hidden">{editingUser ? 'Update' : 'Create'}</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Admin Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Admin Users</h2>
        </div>
        
        <ResponsiveTable 
          data={adminUsers}
          renderMobileCard={(user, index) => (
            <MobileAdminUserCard
              key={`${user.id}-${index}`}
              user={user}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          )}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {adminUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                        <div className="text-xs text-gray-400">@{user.username}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {user.is_super_admin && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                            Super Admin
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleEdit(user)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ResponsiveTable>

        {adminUsers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No admin users found. Create one to get started.</p>
          </div>
        )}

        {/* Platform Overview Dashboard */}
        <div id="platform-overview" className="mt-6 sm:mt-8 space-y-4 sm:space-y-6">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-4 mb-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                  Platform Overview
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Real-time platform statistics and activity metrics
                </p>
              </div>
              {lastDashboardUpdate && (
                <div className="text-left sm:text-right">
                  <p className="text-xs text-gray-400">
                    Last updated: {lastDashboardUpdate.toLocaleTimeString()}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Auto-refreshes every 5 minutes
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Dashboard Statistics Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Platform Statistics Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Platform Statistics
              </h3>
              {dashboardStatsLoading ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                </div>
              ) : dashboardStats ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Total Students</span>
                    <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {dashboardStats.platform_statistics.total_students.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Active Recruiters</span>
                    <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {dashboardStats.platform_statistics.active_recruiters}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Pending Recruiters</span>
                    <span className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                      {dashboardStats.platform_statistics.pending_recruiters}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Total Applications</span>
                    <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {dashboardStats.platform_statistics.total_applications.toLocaleString()}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-gray-600 dark:text-gray-300">
                    Loading platform statistics...
                  </p>
                </div>
              )}
            </div>

            {/* Recent Activity Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Recent Activity
              </h3>
              {dashboardStatsLoading ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              ) : dashboardStats ? (
                <div className="space-y-4">
                  {/* Priority A: Application Submissions (Most Prominent) */}
                  <div className="border-l-4 border-blue-500 pl-4 py-3 bg-blue-50 dark:bg-blue-900/20 rounded-r-lg">
                    <div className="flex justify-between items-baseline">
                      <p className="text-gray-700 dark:text-gray-300 font-medium">Applications Today</p>
                      <div className="flex items-center gap-2">
                        <span className="text-3xl font-bold text-blue-600">
                          {dashboardStats.recent_activity.applications.today}
                        </span>
                        {dashboardStats.recent_activity.applications.trend === 'up' ? (
                          <span className="text-green-500 text-lg">↑</span>
                        ) : dashboardStats.recent_activity.applications.today < dashboardStats.recent_activity.applications.yesterday ? (
                          <span className="text-red-500 text-lg">↓</span>
                        ) : null}
                      </div>
                    </div>
                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                      This week: {dashboardStats.recent_activity.applications.this_week} • Yesterday: {dashboardStats.recent_activity.applications.yesterday}
                    </p>
                  </div>

                  {/* Option B: Recruiter Actions */}
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                      Recruiter Actions Today: <span className="text-xl font-bold text-gray-900 dark:text-white">{dashboardStats.recent_activity.recruiter_actions.today}</span>
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {dashboardStats.recent_activity.recruiter_actions.breakdown.offers > 0 && (
                        <div className="text-green-600 dark:text-green-400">
                          ✓ {dashboardStats.recent_activity.recruiter_actions.breakdown.offers} offer{dashboardStats.recent_activity.recruiter_actions.breakdown.offers > 1 ? 's' : ''}
                        </div>
                      )}
                      {dashboardStats.recent_activity.recruiter_actions.breakdown.shortlisted > 0 && (
                        <div className="text-blue-600 dark:text-blue-400">
                          ⭐ {dashboardStats.recent_activity.recruiter_actions.breakdown.shortlisted} shortlisted
                        </div>
                      )}
                      {dashboardStats.recent_activity.recruiter_actions.breakdown.apply_invites > 0 && (
                        <div className="text-purple-600 dark:text-purple-400">
                          📨 {dashboardStats.recent_activity.recruiter_actions.breakdown.apply_invites} profile request{dashboardStats.recent_activity.recruiter_actions.breakdown.apply_invites > 1 ? 's' : ''}
                        </div>
                      )}
                      {dashboardStats.recent_activity.recruiter_actions.breakdown.interview_invites > 0 && (
                        <div className="text-orange-600 dark:text-orange-400">
                          🎤 {dashboardStats.recent_activity.recruiter_actions.breakdown.interview_invites} interview invite{dashboardStats.recent_activity.recruiter_actions.breakdown.interview_invites > 1 ? 's' : ''}
                        </div>
                      )}
                      {dashboardStats.recent_activity.recruiter_actions.breakdown.external_links > 0 && (
                        <div className="text-gray-600 dark:text-gray-400">
                          🔗 {dashboardStats.recent_activity.recruiter_actions.breakdown.external_links} external link{dashboardStats.recent_activity.recruiter_actions.breakdown.external_links > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Option C: Email Activity */}
                  <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 dark:text-gray-300 font-medium">Emails Sent Today</span>
                      <span className="text-xl font-bold text-orange-600 dark:text-orange-400">
                        {dashboardStats.recent_activity.emails.sent_today}
                      </span>
                    </div>
                    {dashboardStats.recent_activity.emails.sent_today > 0 && (
                      <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                        {dashboardStats.recent_activity.emails.delivery_rate}% delivery rate ({dashboardStats.recent_activity.emails.delivered} delivered)
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-gray-600 dark:text-gray-300">
                    Loading recent activity...
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* System Status Card - Full Width */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              System Status
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="text-gray-500 text-sm mb-2">Database</div>
                <div className="text-lg font-medium text-gray-400">Coming Soon</div>
              </div>
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="text-gray-500 text-sm mb-2">File Storage</div>
                <div className="text-lg font-medium text-gray-400">Coming Soon</div>
              </div>
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="text-gray-500 text-sm mb-2">Email Service</div>
                <div className="text-lg font-medium text-gray-400">Coming Soon</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </AdminRouteGuard>
  );
};

export default AdminUserManagement;